import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { Alert, Box, Button, Chip, EmptyState, Pagination, Spinner, Tabs, TextField, Typography } from '@glplus/glpweb-dsl';
import { useTranslation } from 'react-i18next';
import { useVendor } from '../lib/CapabilitiesContext';
import { useLocationScope } from '../lib/LocationScopeContext';
import { saveBrowserDownload, formatShortDate, formatTime } from '../lib/download';
import { vendorApiClient, type VendorOrderPhase, type VendorOrdersResponse } from '../lib/vendorApiClient';

const EMPTY: VendorOrdersResponse = {
  page: 1, pageSize: 25, total: 0,
  summary: { preliminary: 0, final: 0, preparing: 0, ready: 0, handed_off: 0 },
  locations: [], items: [],
};
const BORDER = '#DDE4E0';
const MUTED = '#5B6573';
const ORANGE = '#A85200';
type Filter = VendorOrderPhase | 'all';

export default function Orders() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { activeVendorId, has } = useVendor();
  const { scope } = useLocationScope();
  const [filter, setFilter] = useState<Filter>('all');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  const refresh = useCallback(async () => {
    if (!activeVendorId) return;
    setLoading(true);
    setError(null);
    try {
      setData(await vendorApiClient.listOrders(activeVendorId, {
        storeId: scope === 'all' ? undefined : scope,
        status: filter,
        q: query.trim() || undefined,
        page,
        pageSize: 25,
      }));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t('orders.loadError'));
    } finally {
      setLoading(false);
    }
  }, [activeVendorId, filter, page, query, scope, t]);

  useEffect(() => { void refresh(); }, [refresh]);
  useEffect(() => { setPage(1); }, [filter, query, scope]);

  const hasPrintable = data.items.some((item) => item.phase !== 'preliminary' && item.phase !== 'handed_off');
  const downloadVisiblePrep = async () => {
    if (!activeVendorId || !hasPrintable) return;
    setDownloading(true);
    setError(null);
    try {
      const file = await vendorApiClient.downloadOrderPrepPdfs(activeVendorId, data.items.filter((item) => item.phase !== 'preliminary' && item.phase !== 'handed_off').map((item) => item.orderLocationId));
      saveBrowserDownload(file.blob, file.filename);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t('orders.downloadError'));
    } finally {
      setDownloading(false);
    }
  };

  const pages = Math.max(1, Math.ceil(data.total / data.pageSize));
  const tabs: Filter[] = ['all', 'preliminary', 'final', 'preparing', 'ready', 'handed_off'];

  return (
    <Box data-testid="vendor-orders" sx={{ maxWidth: 1120 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800 }}>{t('orders.title')}</Typography>
          <Typography sx={{ color: MUTED, mt: 0.5 }}>{t('orders.subtitle')}</Typography>
        </Box>
        <Button ariaLabel={t('orders.downloadPrep')} buttonText={downloading ? t('orders.downloading') : t('orders.downloadPrep')} variant="outlinedOrange" startIcon="download" onClick={() => void downloadVisiblePrep()} disabled={downloading || !hasPrintable} />
      </Box>

      <Alert severity="info" title={t('orders.lockTitle')} sx={{ mt: 2 }}>{t('orders.lockBody')}</Alert>
      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

      <Box sx={{ mt: 2.5, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <Box sx={{ flex: '1 1 300px', maxWidth: 380 }}>
          <TextField ariaLabel={t('orders.search')} placeholder={t('orders.searchPlaceholder')} value={query} onChange={(event) => setQuery(event.target.value)} size="small" fullWidth />
        </Box>
        <Tabs ariaLabel={t('orders.filter')} value={filter} onChange={(_event, value) => setFilter(value as Filter)} color={ORANGE} tabs={tabs.map((value) => ({ value, label: t(`orders.phase.${value}`), count: value === 'all' ? data.total : data.summary[value] }))} />
      </Box>

      {loading ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><Spinner color="brand" /></Box> : data.items.length === 0 ? (
        <Box sx={{ mt: 2 }}><EmptyState icon="onlineordering" title={t('orders.emptyTitle')} body={t('orders.emptyBody')} backgroundColor="#fff" border={`1px solid ${BORDER}`} borderRadius={10} iconColor={ORANGE} /></Box>
      ) : (
        <Box role="table" aria-label={t('orders.table')} sx={{ mt: 2, bgcolor: '#fff', border: `1px solid ${BORDER}`, borderRadius: '10px', overflow: 'hidden' }}>
          <Box role="row" sx={{ display: { xs: 'none', md: 'grid' }, gridTemplateColumns: '1.6fr 1.5fr 1fr 1.2fr 1fr 32px', gap: 2, px: 2.5, py: 1.5, bgcolor: '#F9FAFB', borderBottom: `1px solid ${BORDER}` }}>
            {['order', 'location', 'items', 'window', 'status'].map((key) => <Typography key={key} role="columnheader" sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '.08em', color: MUTED }}>{t(`orders.columns.${key}`).toUpperCase()}</Typography>)}<span />
          </Box>
          {data.items.map((order) => (
            <Box key={order.orderLocationId} role="row" onClick={() => navigate(`/orders/${order.orderLocationId}`)} onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') navigate(`/orders/${order.orderLocationId}`); }} tabIndex={0} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr auto', md: '1.6fr 1.5fr 1fr 1.2fr 1fr 32px' }, gap: 2, alignItems: 'center', px: 2.5, py: 2, borderBottom: `1px solid ${BORDER}`, cursor: 'pointer', '&:hover': { bgcolor: '#FFF9F4' }, '&:focus-visible': { outline: `2px solid ${ORANGE}`, outlineOffset: -2 } }}>
              <Box role="cell"><Typography sx={{ fontWeight: 800 }}>{order.glpOrderId ?? order.orderLocationId}</Typography><Typography variant="caption" sx={{ color: MUTED }}>{t('orders.bagCode', { code: order.bagCode })}</Typography></Box>
              <Typography role="cell" sx={{ display: { xs: 'none', md: 'block' } }}>{order.location.name}</Typography>
              <Typography role="cell" sx={{ display: { xs: 'none', md: 'block' } }}>{t('orders.itemCount', { count: order.itemCount })}</Typography>
              <Box role="cell" sx={{ display: { xs: 'none', md: 'block' } }}><Typography>{formatShortDate(order.runDate)}</Typography><Typography variant="caption" sx={{ color: MUTED }}>{formatTime(order.deliveryWindowStart)} – {formatTime(order.deliveryWindowEnd)}</Typography></Box>
              <Box role="cell"><Chip label={t(`orders.phase.${order.phase}`)} size="small" backgroundColor={phaseColor(order.phase).bg} textColor={phaseColor(order.phase).text} /></Box>
              <Typography aria-hidden sx={{ color: ORANGE, fontWeight: 800 }}>›</Typography>
            </Box>
          ))}
        </Box>
      )}

      {pages > 1 && <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}><Pagination page={page} totalPages={pages} onPageChange={setPage} ariaLabel={t('orders.pagination')} activeBgColor="#25862D" activeColor="#fff" /></Box>}
      {!has('order:fulfill') && <Typography variant="caption" sx={{ display: 'block', color: MUTED, mt: 2 }}>{t('orders.readOnly')}</Typography>}
    </Box>
  );
}

function phaseColor(phase: VendorOrderPhase) {
  if (phase === 'ready') return { bg: '#E9F6EA', text: '#216E27' };
  if (phase === 'preparing') return { bg: '#FFF1E3', text: '#9A4B00' };
  if (phase === 'handed_off') return { bg: '#EEF1F8', text: '#343F5B' };
  return { bg: '#F2F4F5', text: '#4E5A64' };
}
